import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, errorMessage } from './errors';

/** Repete só falhas transitórias (rede e 5xx), no máximo 2 vezes. Erro do usuário (4xx) não repete. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError) return error.isNetworkError || error.isServerError;
  return false;
}

/**
 * Espera antes de repetir: a que o servidor pediu (Retry-After, quando está sobrecarregado — T-254; no máximo 30 s)
 * ou a padrão do TanStack Query (1 s, 2 s, 4 s… até 30 s).
 */
export function retryDelay(failureCount: number, error: unknown): number {
  if (error instanceof ApiError && error.retryAfterSeconds)
    return Math.min(error.retryAfterSeconds * 1000, 30_000);
  return Math.min(1000 * 2 ** failureCount, 30_000);
}

/**
 * Tratamento global de erros:
 *  - mutações (salvar, excluir…) que falham mostram um toast com a mensagem do servidor, a menos
 *    que a tela trate o erro (meta.handlesError);
 *  - consultas que já tinham dados e falham ao atualizar em segundo plano mostram um toast;
 *    a primeira carga com erro é mostrada na própria tela (ErrorState).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.state.data !== undefined) {
          toast.error('Não foi possível atualizar os dados', { description: errorMessage(error) });
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        if (mutation.meta?.handlesError) return;
        toast.error('Não foi possível concluir a ação', { description: errorMessage(error) });
      },
    }),
    defaultOptions: {
      queries: { retry: shouldRetry, retryDelay, staleTime: 30_000, refetchOnWindowFocus: true },
      mutations: { retry: false },
    },
  });
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { handlesError?: boolean };
  }
}
